import React, { useState } from 'react';
import SharedSaleBadge from '../SharedSaleBadge';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { formatSegmentKey, hasAnyPaidAdmittedParticipant } from '../../utils/tripPricingDisplay';

const formatRate = (rate) => `$${Number(rate).toFixed(2).replace(/\.00$/, '')}`;

function getLegRouteLabel(leg) {
  if (leg.isFullRoute) return 'Full route';
  if (leg.pickup && leg.dropoff) return `${leg.pickup} → ${leg.dropoff}`;
  return leg.label?.replace(' (Full route)', '') ?? formatSegmentKey(leg.value);
}

function formatLegChipLabel(leg, legEstimates, isSelected) {
  const routeLabel = getLegRouteLabel(leg);
  if (isSelected) return routeLabel;
  const estimate = legEstimates.find((e) => e.value === leg.value);
  if (estimate?.valid) {
    return `${routeLabel} · ${formatRate(estimate.currentRate)}`;
  }
  return routeLabel;
}

const NextRiderDelta = ({
  currentRate,
  projectedRate,
  savingsToNext,
  ridersToNext,
}) => {
  const nextLabel = ridersToNext === 1 ? '+1 rider' : `+${ridersToNext} riders`;

  return (
    <div className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="text-center min-w-[3.5rem]">
          <p className="text-lg font-bold text-[#A44432]">{formatRate(currentRate)}</p>
          <p className="text-[10px] text-stone-500 uppercase tracking-wide">now</p>
        </div>
        <ArrowRight size={18} className="text-stone-400 shrink-0" aria-hidden />
        <div className="text-center min-w-[3.5rem]">
          <p className="text-lg font-semibold text-[#2D1E17]">{formatRate(projectedRate)}</p>
          <p className="text-[10px] text-stone-500 uppercase tracking-wide">{nextLabel}</p>
        </div>
        <span className="text-sm font-semibold text-[#2D5A27] ml-auto">
          -{formatRate(savingsToNext)} each
        </span>
      </div>
    </div>
  );
};

const PriceLadderBars = ({ priceLadder }) => {
  if (!priceLadder?.length) return null;

  const maxRate = priceLadder[0]?.rate || 1;

  return (
    <div className="rounded-xl border border-stone-100 bg-stone-50 p-3 mt-2 space-y-2">
      {priceLadder.map(({ seats, rate, active, next }) => {
        const widthPct = Math.max(8, (rate / maxRate) * 100);
        let barClass = 'bg-stone-200';
        if (active) barClass = 'bg-[#A44432]';
        else if (next) barClass = 'bg-stone-100 border border-dashed border-stone-400';

        return (
          <div key={seats} className="flex items-center gap-2">
            <span
              className={`w-6 text-xs font-bold tabular-nums shrink-0 ${
                active ? 'text-[#A44432]' : 'text-stone-500'
              }`}
            >
              {seats}
            </span>
            <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden min-w-0">
              <div
                className={`h-full rounded-full transition-all ${barClass}`}
                style={{ width: `${widthPct}%` }}
              />
            </div>
            <span
              className={`text-xs font-semibold tabular-nums shrink-0 w-12 text-right ${
                active ? 'text-[#A44432]' : 'text-stone-600'
              }`}
            >
              {formatRate(rate)}
            </span>
            {active && (
              <span className="text-[9px] font-bold text-[#A44432] uppercase w-8 shrink-0">
                You
              </span>
            )}
            {!active && next && (
              <span className="text-[9px] font-bold text-stone-500 uppercase w-8 shrink-0">
                Next
              </span>
            )}
            {!active && !next && <span className="w-8 shrink-0" aria-hidden />}
          </div>
        );
      })}
    </div>
  );
};

const TripPriceSeatsBlock = ({
  trip,
  legOptions = [],
  legEstimates = [],
  selectedLegValue,
  onLegChange,
  isJoined = false,
  isCreator = false,
  viewerSegment = null,
}) => {
  const [ladderOpen, setLadderOpen] = useState(false);

  const selectedEstimate = legEstimates.find((leg) => leg.value === selectedLegValue);
  const seatsFilled = trip?.seats_filled ?? 0;
  const showLegChips = legOptions.length > 1 && !isJoined && !isCreator;
  const anyPaidRider = hasAnyPaidAdmittedParticipant(trip?.participants);

  if (isJoined) {
    const segmentLabel = viewerSegment ? formatSegmentKey(viewerSegment) : null;
    const viewerEstimate = viewerSegment
      ? legEstimates.find((leg) => leg.value === viewerSegment)
      : null;

    return (
      <div className="space-y-2">
        <p className="text-[10px] text-stone-500 uppercase font-bold tracking-wider">
          Your booking
        </p>
        {segmentLabel && (
          <p className="text-sm font-semibold text-[#2D1E17]">Your leg: {segmentLabel}</p>
        )}
        {viewerEstimate?.valid && (
          <p className="text-sm text-stone-600">
            {formatRate(viewerEstimate.currentRate)} / person estimate at {seatsFilled} rider
            {seatsFilled !== 1 ? 's' : ''} on vehicle
          </p>
        )}
        <p className="text-xs text-stone-500">See My Trips for your confirmed booking details.</p>
      </div>
    );
  }

  if (isCreator) {
    return (
      <div className="space-y-1">
        <p className="text-[10px] text-stone-500 uppercase font-bold tracking-wider">
          Pricing
        </p>
        <p className="text-sm text-stone-600">
          Per-person price varies by which leg each rider joins.
        </p>
      </div>
    );
  }

  if (!selectedEstimate?.valid) {
    return (
      <p className="text-sm text-stone-500">Pricing shown when you join.</p>
    );
  }

  const {
    currentRate,
    projectedRate,
    savingsToNext,
    hasRoomForMore,
    priceLadder,
  } = selectedEstimate;

  const selectedLeg = legOptions.find((leg) => leg.value === selectedLegValue);
  const selectedRouteLabel = selectedLeg
    ? getLegRouteLabel(selectedLeg)
    : formatSegmentKey(selectedLegValue);

  const ridersToNext = hasRoomForMore
    ? selectedEstimate.nextSeats - selectedEstimate.currentSeats
    : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-[#2D1E17]">{selectedRouteLabel}</p>
        <span className="text-[10px] font-medium text-stone-500 uppercase tracking-wide px-1.5 py-0.5 rounded border border-stone-200 bg-white">
          Estimate
        </span>
      </div>

      {showLegChips && (
        <div className="flex flex-wrap gap-1.5">
          {legOptions.map((leg) => {
            const isSelected = selectedLegValue === leg.value;
            return (
              <button
                key={leg.value}
                type="button"
                onClick={() => onLegChange?.(leg.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  isSelected
                    ? 'bg-[#A44432] text-white border-[#A44432]'
                    : 'bg-white text-stone-600 border-stone-200 hover:border-[#A44432]/50'
                }`}
              >
                {formatLegChipLabel(leg, legEstimates, isSelected)}
              </button>
            );
          })}
        </div>
      )}

      <SharedSaleBadge trip={trip} segment={selectedLegValue} />
      <p className="text-lg font-semibold text-[#2D1E17]">
        Join now for{' '}
        <span className="text-xl font-bold text-[#A44432]">{formatRate(currentRate)}</span>
        <span className="text-sm font-normal text-stone-500"> / person</span>
        {!anyPaidRider && <span className="text-sm font-normal text-stone-500"> or less</span>}
      </p>
      <p className="text-xs text-stone-500 -mt-1">
        {seatsFilled} rider{seatsFilled !== 1 ? 's' : ''} on vehicle today
      </p>

      {hasRoomForMore && savingsToNext > 0 && !anyPaidRider && (
        <NextRiderDelta
          currentRate={currentRate}
          projectedRate={projectedRate}
          savingsToNext={savingsToNext}
          ridersToNext={ridersToNext}
        />
      )}

      <p className="text-xs text-stone-500">
        Final price confirmed when you choose your leg at join.
      </p>

      {priceLadder.length > 0 && !anyPaidRider && (
        <div>
          <button
            type="button"
            onClick={() => setLadderOpen((open) => !open)}
            className="flex items-center gap-1 text-xs font-medium text-[#A44432] hover:underline"
          >
            {ladderOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            How price drops with more riders
          </button>
          {ladderOpen && <PriceLadderBars priceLadder={priceLadder} />}
        </div>
      )}
    </div>
  );
};

export default TripPriceSeatsBlock;
